'use client'

import { Link, usePathname, useRouter } from '@/shared/navigation'
import { useEffect, useMemo, useState } from 'react'
import {
  buildAttendanceSummaryFromRoster,
  mapStaffFromApi,
  mapAttendanceRecordsFromApi,
} from '@/features/kitchen/api/mapAttendanceFromApi'
import { buildProductionSummary } from '@/features/kitchen/api/buildProductionSummary'
import { buildDispatchSummaryFromShipments, mapDeliveriesFromApi } from '@/features/kitchen/api/mapDispatchFromApi'
import { fetchTodayAttendance } from '@/features/kitchen/api/getTodayAttendance'
import { fetchTodayDeliveries } from '@/features/kitchen/api/getTodayDispatch'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import {
  fetchTodayProductionPlan,
  fetchTodayProductionSessions,
} from '@/features/kitchen/api/getTodayProduction'
import { buildWasteSummaryFromBatches, mapWasteBatchesFromApi } from '@/features/kitchen/api/mapWasteFromApi'
import { fetchTodayWasteBatches } from '@/features/kitchen/api/getTodayWaste'
import { getKitchenHomeSummary, type HomeActionPriority } from '@/features/kitchen/storage/homeSummary'
import { todayDateKey } from '@/features/kitchen/storage/attendanceStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { clearKitchenSession, getKitchenSession } from '@/shared/auth/sessionStorage'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { EmptyState } from '@/shared/components/EmptyState'

const MODULE_ICONS = {
  attendance: 'face_retouching_natural',
  production: 'bakery_dining',
  dispatch: 'local_shipping',
  waste: 'delete_sweep',
} as const

function priorityLabel(priority: HomeActionPriority): string {
  if (priority === 'high') return 'Prioritas'
  if (priority === 'medium') return 'Perlu dicek'
  return 'Info'
}

function priorityBadgeClass(priority: HomeActionPriority): string {
  if (priority === 'high') return 'badge-danger'
  if (priority === 'medium') return 'badge-warning'
  return 'badge-draft'
}

function sessionRoleLabel(role: 'HEAD_KITCHEN' | 'STAFF_KITCHEN'): string {
  return role === 'HEAD_KITCHEN' ? 'Kepala dapur' : 'Staff dapur'
}

export function KitchenHomePage() {
  const pathname = usePathname()
  const router = useRouter()
  const session = getKitchenSession()
  const device = getKitchenDevice()
  const [refreshKey, setRefreshKey] = useState(0)
  const [apiOverrides, setApiOverrides] = useState<Parameters<typeof getKitchenHomeSummary>[1]>(undefined)
  const useApi = shouldUseKitchenApi()

  const summary = useMemo(
    () => getKitchenHomeSummary(undefined, apiOverrides),
    [pathname, refreshKey, apiOverrides],
  )

  useEffect(() => {
    if (!useApi || !device?.deviceId) {
      setApiOverrides(undefined)
      return
    }

    let cancelled = false
    const date = todayDateKey()

    Promise.all([
      fetchTodayProductionPlan(device.deviceId),
      fetchTodayProductionSessions(device.deviceId),
      fetchTodayDeliveries(device.deviceId),
      fetchTodayWasteBatches(device.deviceId),
      fetchTodayAttendance(device.deviceId),
    ])
      .then(([plan, sessions, deliveries, waste, attendance]) => {
        if (cancelled) return
        const roster = mapStaffFromApi(attendance.staff)
        const records = mapAttendanceRecordsFromApi(attendance.staff, date)
        const dispatchShipments = mapDeliveriesFromApi(deliveries.deliveries)
        const wasteBatches = mapWasteBatchesFromApi(waste.batches)

        setApiOverrides({
          production: buildProductionSummary(plan, sessions.sessions),
          dispatch: buildDispatchSummaryFromShipments(dispatchShipments),
          waste: buildWasteSummaryFromBatches(wasteBatches),
          attendance: buildAttendanceSummaryFromRoster(roster, records),
        })
      })
      .catch(() => {
        if (!cancelled) setApiOverrides(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, refreshKey])

  useEffect(() => {
    if (!useApi) return undefined
    const bump = () => setRefreshKey((k) => k + 1)
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump()
    }
    window.addEventListener('focus', bump)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', bump)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [useApi])

  const todayLabel = new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date())

  const menus = [
    {
      to: '/kitchen/production',
      title: kitchenTerms.modules.production,
      subtitle: `${kitchenTerms.production.plan} ${summary.production.planPct}% · ${summary.production.openSessionCount} sesi terbuka`,
      icon: 'bakery_dining',
    },
    {
      to: '/kitchen/dispatch',
      title: `${kitchenTerms.modules.dispatch} ke toko`,
      subtitle: `${summary.dispatch.pendingCount} pengiriman perlu tindakan`,
      icon: 'local_shipping',
    },
    {
      to: '/kitchen/stock',
      title: kitchenTerms.modules.stock,
      subtitle: 'Good stock, waste stock & bahan baku',
      icon: 'inventory_2',
    },
    {
      to: '/kitchen/kitchen-transfer',
      title: 'Kirim ke dapur lain',
      subtitle: 'Transfer stok bagus antar dapur',
      icon: 'swap_horiz',
    },
    {
      to: '/kitchen/kitchen-transfer/receive',
      title: 'Terima dari dapur lain',
      subtitle: 'Scan QR delman lalu konfirmasi',
      icon: 'move_to_inbox',
    },
    {
      to: '/kitchen/waste',
      title: kitchenTerms.modules.waste,
      subtitle: `${summary.waste.readyExecuteCount} siap dieksekusi`,
      icon: 'delete_sweep',
    },
    {
      to: '/kitchen/attendance',
      title: `${kitchenTerms.modules.attendance} tim`,
      subtitle: `${summary.attendance.checkedIn}/${summary.attendance.total} sudah ${kitchenTerms.attendance.checkInVerb}`,
      icon: 'face_retouching_natural',
    },
  ] as const

  function handleSwitchSession() {
    clearKitchenSession()
    router.replace('/kitchen/login')
  }

  return (
    <section className="space-y-4">
      <header className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-headline-lg-mobile text-primary truncate">{device?.kitchenName ?? 'Dapur'}</h1>
          <p className="text-body-sm text-on-surface-variant capitalize mt-0.5">{todayLabel}</p>
        </div>
        <button
          type="button"
          className="btn-outline shrink-0 min-w-[44px] px-3"
          onClick={() => setRefreshKey((value) => value + 1)}
          aria-label="Perbarui ringkasan"
          title="Perbarui ringkasan"
        >
          <span className="material-symbols-outlined">refresh</span>
        </button>
      </header>

      <div className="card">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-body-lg font-bold text-on-surface">Perlu tindakan hari ini</p>
          <span className="badge-warning">{summary.actions.length} item</span>
        </div>
        {summary.actions.length === 0 ? (
          <EmptyState
            compact
            icon="task_alt"
            title="Semua modul sudah rapi"
            description="Tidak ada yang perlu tindakan. Cek kembali setelah ada aktivitas baru."
          />
        ) : (
          <ul className="space-y-2">
            {summary.actions.map((action) => (
              <li key={action.id}>
                <Link
                  href={action.to}
                  className="flex items-start gap-3 rounded-lg border border-outline-variant p-3 hover:border-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-primary mt-0.5">
                    {MODULE_ICONS[action.module]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-body-sm font-semibold text-on-surface">{action.title}</p>
                      <span className={priorityBadgeClass(action.priority)}>{priorityLabel(action.priority)}</span>
                    </div>
                    <p className="text-body-sm text-on-surface-variant mt-0.5">{action.detail}</p>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="card p-3">
          <p className="text-label-md text-on-surface-variant">{kitchenTerms.modules.attendance}</p>
          <p className="text-headline-md text-primary mt-1">
            {summary.attendance.checkedIn}/{summary.attendance.total}
          </p>
          <p className="text-body-sm text-on-surface-variant mt-1">{kitchenTerms.attendance.checkInVerb}</p>
        </div>
        <div className="card p-3">
          <p className="text-label-md text-on-surface-variant">{kitchenTerms.modules.production}</p>
          <p className="text-headline-md text-primary mt-1">{summary.production.planPct}%</p>
          <p className="text-body-sm text-on-surface-variant mt-1">{kitchenTerms.production.plan.toLowerCase()}</p>
        </div>
        <div className="card p-3">
          <p className="text-label-md text-on-surface-variant">{kitchenTerms.modules.dispatch}</p>
          <p className="text-headline-md text-primary mt-1">{summary.dispatch.pendingCount}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">belum selesai</p>
        </div>
        <div className="card p-3">
          <p className="text-label-md text-on-surface-variant">{kitchenTerms.modules.waste}</p>
          <p className="text-headline-md text-primary mt-1">{summary.waste.readyExecuteCount}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">siap eksekusi</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {menus.map((item) => (
          <Link key={item.to} href={item.to} className="home-tile">
            <span className="material-symbols-outlined text-primary text-[28px]">{item.icon}</span>
            <h2 className="text-body-lg font-bold text-on-surface leading-snug mt-2">{item.title}</h2>
            <p className="text-body-sm text-on-surface-variant mt-1">{item.subtitle}</p>
          </Link>
        ))}
      </div>

      <div className="rounded-lg border border-outline-variant/60 bg-surface-container/40 p-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm text-on-surface-variant">
          <span>
            Perangkat: <span className="font-semibold text-on-surface">{device?.deviceName ?? '—'}</span>
          </span>
          <span>
            Sesi:{' '}
            <span className="font-semibold text-on-surface">
              {session?.userName ?? '—'}
              {session ? ` (${sessionRoleLabel(session.role)})` : ''}
            </span>
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-caption text-on-surface-variant">
            Login tablet hanya sesi operator · absensi tim di menu Absensi.
          </p>
          <button
            type="button"
            className="text-label-md text-primary shrink-0 min-h-[44px]"
            onClick={handleSwitchSession}
          >
            Ganti sesi
          </button>
        </div>
      </div>
    </section>
  )
}
